import React, {useEffect, useMemo, useState} from 'react'
import Highcharts from 'highcharts'
import HighchartsHeatmap from "highcharts/modules/heatmap";

import HighchartsReact from 'highcharts-react-official'
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import "./report_style.css";

import { useLocation } from "react-router-dom";
import ProcessService from "../../../services/process.service";
import {toast} from "react-toastify";
import Grid from "@mui/material/Grid";
import SoftButton from "../../../components/SoftButton";
import Icon from "@mui/material/Icon";
import { jsPDF } from "jspdf";

import html2canvas from 'html2canvas'

const AH_BANDS = [
    { key: "AH5", max: 5, color: "#ff0000", label: "AH5 (<= 5 years)" },
    { key: "AH4", max: 10, color: "#ffa500", label: "AH4 (<= 10 years)" },
    { key: "AH3", max: 25, color: "#ffff00", label: "AH3 (<= 25 years)" },
    { key: "AH2", max: 40, color: "#adff2f", label: "AH2 (<= 40 years)" },
    { key: "AH1", max: Number.POSITIVE_INFINITY, color: "#008000", label: "AH1 (> 40 years)" },
];

const getAhBand = (value) => {
    for (let i = 0; i < AH_BANDS.length; i += 1) {
        if (value <= AH_BANDS[i].max) {
            return AH_BANDS[i];
        }
    }
    return AH_BANDS[AH_BANDS.length - 1];
};

function ReportViewer() {

    const search = useLocation().search;
    HighchartsHeatmap(Highcharts);
    const [recordCount, setRecordCount] = useState(10)
    const [failedYear, setFailedYear] = useState(2010)
    const [years, setYears] = useState([])
    const [mergeYears, setMergeYears] = useState({})
    const [heatMaps, setHeatMaps] = useState([])
    const [inspectionYear, setInspectionYear] = useState({})
    const [failures, setFailures] = useState([])
    const [observations, setObservations] = useState([])
    const [selectedAhBands, setSelectedAhBands] = useState(AH_BANDS.map((band) => band.key))
    const selectedAhBandSet = useMemo(() => new Set(selectedAhBands), [selectedAhBands]);
    const filteredHeatMaps = useMemo(
        () => heatMaps.filter((item) => selectedAhBandSet.has(getAhBand(item[2]).key)),
        [heatMaps, selectedAhBandSet]
    );
    const heatMapRange = useMemo(() => {
        const values = heatMaps.map((item) => item[2]).filter((value) => Number.isFinite(value));
        if (!values.length) {
            return { min: 0, max: 1 };
        }
        const min = Math.min(...values);
        const max = Math.max(...values);
        return { min, max: min === max ? min + 1 : max };
    }, [heatMaps]);
    const chartFontFamily = "Manrope, Avenir Next, Segoe UI, sans-serif";
    const option_failures = {
            title: { "text": "Failure Projection" },
            chart:{
                "type":"column",
                backgroundColor: "transparent",
                style: { fontFamily: chartFontFamily }
            } ,
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0,
                    dataLabels: {
                        enabled: true,
                        crop: false,
                        overflow: 'none'
                    }
                }
            },
            loading: {
                labelStyle: {
                    top: '35%',
                    fontSize: "2em"
                }
            },


            legend: {
                padding: 0,
                margin: 5
            },
            credits: { enabled: false },
            series: [ { data: failures, "name":"Failures" }],
            xAxis: {
                categories: years,
                crosshair: true,
                labels: {
                    format: '{value}'
                }
            },
            yAxis:{
                min: 0,
                title: {
                    text: 'Failure Frequency'
                }
            }
        };
    const option_analytics = {
            title: { "text": "Asset Health Predictive Analytics" },
            chart: {
                type: 'heatmap',
                marginTop: 40,
                marginBottom: 80,
                plotBorderWidth: 0,
                backgroundColor: "transparent",
                style: { fontFamily: chartFontFamily }
            },

            series: [{
                name: 'Heat Maps',
                data: filteredHeatMaps,
                tooltip: {
                    pointFormatter: function () {
                        const value = this.value;
                        const label = getAhBand(value).label;
                        return `Years-to-failure: <b>${Number(value).toFixed(2)}</b><br/>${label}`;
                    }
                }
            }],
            colorAxis : {
                min: heatMapRange.min,
                max: heatMapRange.max,
                stops: [
                    [0, "#ff0000"],
                    [0.25, "#ffa500"],
                    [0.5, "#ffff00"],
                    [0.75, "#adff2f"],
                    [1, "#008000"]
                ]
            },
            xAxis: {
                categories: years,
                crosshair: true,
            },
            yAxis:{
                title: {
                    text: 'Segment Number'
                }
            },

            legend: {
                enabled: false
            },
            credits: { enabled: false },


        };
    const option_mergers = {
            title: { "text": 'Asset Age Distribution' },
            chart:{
                "type":"column",
                backgroundColor: "transparent",
                style: { fontFamily: chartFontFamily }
            } ,
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0
                }
            },
            series: [  { data: Object.values(mergeYears) , name: "Count"}],
            xAxis: {
                categories:  Object.keys(mergeYears) ,
                crosshair: true,
                labels: {
                    format: '{value} Years'
                }

            },
            yAxis:{
                min: 0,
                title: {
                    text: 'Number of Components'
                }
            },
            credits: { enabled: false }
        };
    const option_line = {
            title: { "text": 'RBI Predictive Analytics' },
            chart: {
                backgroundColor: "transparent",
                style: { fontFamily: chartFontFamily }
            },
            plotOptions: {
                // column: {
                //     pointPadding: 0.2,
                //     borderWidth: 0
                // }
            },
            series: [
                // { data: Object.values(inspectionYear) , name: "Do Nothing"} ,
                {
                    data: Object.values(inspectionYear) ,
                    name: "Inspection Powered",
                    threshold: 1,
                    // zones: [{
                    //     value: 1,
                    //     fillColor: 'green',
                    //     color: 'green'
                    // },{
                    //     value: 4,
                    //     fillColor: 'orange',
                    //     color: 'red'
                    // }],


                }
            ],
            xAxis: {
                categories:   years ,
                crosshair: true,

            },
            yAxis: {
                label:   "years" ,
                name:   "years" ,
                title: {
                    text: 'Failure Frequency'
                }
            },
            credits: { enabled: false }

        };
    const parseCsvRows = (text) => text
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "")
        .map((line) => line.split(",").map((cell) => cell.trim()));

    const loadData = (id) =>{
        if (!id) {
            return;
        }
        ProcessService.getProcessItem(id)
            .then((data) => {
                const outputUrl = data.data.data["output_report_path"];
                const onlyUrl = data.data.data["only_report_path"];
                return Promise.all([fetch(onlyUrl), fetch(outputUrl)]);
            })
            .then(async ([onlyResponse, outputResponse]) => {
                if (!onlyResponse.ok || !outputResponse.ok) {
                    throw new Error("report data is not available");
                }

                const [onlyText, outputText] = await Promise.all([onlyResponse.text(), outputResponse.text()]);
                const outputRows = parseCsvRows(outputText);
                const failuresByYear = {};
                outputRows.slice(1).forEach((row) => {
                    const year = Number.parseInt(row[2], 10);
                    const frequency = Number.parseFloat(row[1]);
                    if (Number.isFinite(year) && Number.isFinite(frequency)) {
                        failuresByYear[year] = frequency;
                    }
                });
                const outputYears = Object.keys(failuresByYear)
                    .map((year) => Number.parseInt(year, 10))
                    .filter((year) => Number.isFinite(year))
                    .sort((first, second) => first - second);
                const applyOutputOnlyFallback = (reason) => {
                    if (!outputYears.length) {
                        throw new Error("empty report data");
                    }
                    const inspectionSeries = {};
                    outputYears.forEach((year) => {
                        inspectionSeries[year] = failuresByYear[year] || 0;
                    });
                    const failureSeries = outputYears.map((year) => failuresByYear[year] || 0);
                    let predictedFailureYear = outputYears[outputYears.length - 1];
                    outputYears.forEach((year) => {
                        if (predictedFailureYear === outputYears[outputYears.length - 1] && inspectionSeries[year] >= 1) {
                            predictedFailureYear = year;
                        }
                    });

                    const aiObservations = [
                        `Asset-level rows were not available in this report output (${reason}).`,
                        `System-level projection is shown using ${outputYears.length} yearly RBI points from the output dataset (API RP 580/581 context).`,
                        `Highest projected system failure frequency is ${Math.max(...failureSeries, 0).toFixed(4)}.`,
                        `Predicted first system-level threshold crossing (>= 1.0 cumulative frequency) is ${predictedFailureYear}.`,
                        `Final inspection interval and disposition should be checked against applicable code/program constraints (API 510/570/653 or CSA Z662, plus API 579 where degradation is confirmed).`,
                    ];

                    setYears(outputYears.map((year) => String(year)));
                    setMergeYears({});
                    setHeatMaps([]);
                    setInspectionYear(inspectionSeries);
                    setFailures(failureSeries);
                    setRecordCount(0);
                    setFailedYear(predictedFailureYear || new Date().getFullYear());
                    setObservations(aiObservations);
                };
                const onlyRows = parseCsvRows(onlyText);
                if (onlyRows.length < 2) {
                    applyOutputOnlyFallback("only.csv is header-only");
                    return;
                }

                const headerRows = onlyRows[0].map((header, index) => {
                    if (index === 0) {
                        return header.replace(/^\uFEFF/, "");
                    }
                    return header;
                });

                const installYearIndex = headerRows.findIndex((header) => /install\s*year/i.test(header));
                const segmentNumberIndex = headerRows.findIndex((header) => /segment number/i.test(header));
                const subComponentIndex = headerRows.findIndex((header) => /sub-component/i.test(header));
                const pofCandidates = headerRows
                    .map((header, index) => {
                        const normalizedHeader = (header || "").replace(/^\uFEFF/, "");
                        if (!/pof/i.test(normalizedHeader)) {
                            return null;
                        }
                        const yearMatch = normalizedHeader.match(/(19|20)\d{2}/);
                        if (!yearMatch) {
                            return null;
                        }
                        let priority = 0;
                        if (/leak/i.test(normalizedHeader)) {
                            priority = 2;
                        } else if (/rupture|burst/i.test(normalizedHeader)) {
                            priority = 1;
                        }
                        return {
                            index,
                            year: parseInt(yearMatch[0], 10),
                            priority,
                        };
                    })
                    .filter((item) => item !== null);
                const pofByYear = new Map();
                pofCandidates.forEach((candidate) => {
                    const current = pofByYear.get(candidate.year);
                    if (!current || candidate.priority > current.priority) {
                        pofByYear.set(candidate.year, candidate);
                    }
                });
                const pofColumns = Array.from(pofByYear.values())
                    .sort((first, second) => first.year - second.year)
                    .map(({index, year}) => ({index, year}));

                if (!pofColumns.length) {
                    applyOutputOnlyFallback("POF columns are missing in only.csv");
                    return;
                }

                const nowYear = new Date().getFullYear();
                const nearestYearColumn = pofColumns.reduce((best, current) => {
                    if (!best) {
                        return current;
                    }
                    if (Math.abs(current.year - nowYear) < Math.abs(best.year - nowYear)) {
                        return current;
                    }
                    return best;
                }, null);

                const yearCategories = pofColumns.map((item) => item.year);
                const pofTotals = {};
                const ageDistribution = {};
                const heatMapData = [];
                let validAssetCount = 0;
                let highestCurrentRisk = { value: -1, label: "N/A" };

                onlyRows.slice(1).forEach((row) => {
                    let hasFrequency = false;
                    pofColumns.forEach((column, colIndex) => {
                        const frequency = Number.parseFloat(row[column.index]);
                        if (Number.isFinite(frequency)) {
                            hasFrequency = true;
                            pofTotals[column.year] = (pofTotals[column.year] || 0) + frequency;
                            const yearsToFailure = frequency > 0 ? 1 / frequency : 60;
                            heatMapData.push([colIndex, validAssetCount, yearsToFailure]);
                        }
                    });
                    if (!hasFrequency) {
                        return;
                    }

                    if (installYearIndex >= 0) {
                        const installYear = Number.parseInt(row[installYearIndex], 10);
                        if (Number.isFinite(installYear)) {
                            const age = Math.max(0, nowYear - installYear);
                            ageDistribution[age] = (ageDistribution[age] || 0) + 1;
                        }
                    }

                    if (nearestYearColumn) {
                        const currentRisk = Number.parseFloat(row[nearestYearColumn.index]);
                        if (Number.isFinite(currentRisk) && currentRisk > highestCurrentRisk.value) {
                            const subComponent = subComponentIndex >= 0 ? row[subComponentIndex] : "Asset";
                            const segment = segmentNumberIndex >= 0 ? row[segmentNumberIndex] : `${validAssetCount + 1}`;
                            highestCurrentRisk = {
                                value: currentRisk,
                                label: `${subComponent} ${segment}`.trim(),
                            };
                        }
                    }
                    validAssetCount += 1;
                });

                const sortedAges = Object.fromEntries(
                    Object.entries(ageDistribution).sort((first, second) => parseInt(first[0], 10) - parseInt(second[0], 10))
                );
                const inspectionSeries = {};
                yearCategories.forEach((year) => {
                    inspectionSeries[year] = pofTotals[year] || 0;
                });

                let predictedFailureYear = yearCategories[yearCategories.length - 1];
                yearCategories.forEach((year) => {
                    if (predictedFailureYear === yearCategories[yearCategories.length - 1] && inspectionSeries[year] >= 1) {
                        predictedFailureYear = year;
                    }
                });

                const failureSeries = yearCategories.map((year) => failuresByYear[year] || 0);

                const heatMapValues = heatMapData.map((item) => item[2]).filter((value) => Number.isFinite(value));
                const sortedHeatMapValues = [...heatMapValues].sort((first, second) => first - second);
                const medianHeatMap = sortedHeatMapValues.length
                    ? sortedHeatMapValues[Math.floor(sortedHeatMapValues.length / 2)]
                    : 0;
                let maxSystemFrequencyYear = yearCategories[0];
                let maxSystemFrequencyValue = -1;
                yearCategories.forEach((year) => {
                    if (inspectionSeries[year] > maxSystemFrequencyValue) {
                        maxSystemFrequencyValue = inspectionSeries[year];
                        maxSystemFrequencyYear = year;
                    }
                });

                const aiObservations = [
                    `Analyzed ${validAssetCount} assets across ${yearCategories.length} projected years.`,
                    `Heatmap years-to-failure spans ${sortedHeatMapValues.length ? sortedHeatMapValues[0].toFixed(2) : "0.00"} to ${sortedHeatMapValues.length ? sortedHeatMapValues[sortedHeatMapValues.length - 1].toFixed(2) : "0.00"} (median ${medianHeatMap.toFixed(2)}).`,
                    `Highest projected system failure frequency is ${Math.max(0, maxSystemFrequencyValue).toFixed(4)} in ${maxSystemFrequencyYear} (API RP 580/581 risk trend context).`,
                    `Current highest-risk segment around ${nearestYearColumn ? nearestYearColumn.year : nowYear} is ${highestCurrentRisk.label} (${Math.max(0, highestCurrentRisk.value).toFixed(4)}).`,
                    `Predicted first system-level threshold crossing (>= 1.0 cumulative frequency) is ${predictedFailureYear}.`,
                    `Interpret model pathways against applicable damage mechanisms (API RP 571) and confirm run-repair-replace decisions with API 579-1/ASME FFS-1 where required.`,
                ];

                setYears(yearCategories.map((year) => String(year)));
                setMergeYears(sortedAges);
                setHeatMaps(heatMapData);
                setInspectionYear(inspectionSeries);
                setFailures(failureSeries);
                setRecordCount(validAssetCount);
                setFailedYear(predictedFailureYear || nowYear);
                setObservations(aiObservations);
            })
            .catch((e)=>{
                toast.error(e?.response?.data?.status || e.message || "error in fetch", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                    autoClose: 2000,
                    hideProgressBar: false,
                });
            });
    }
    useEffect(()=>{
        const id = new URLSearchParams(search).get("id");
        loadData(id);
    }, [search])

    const toggleAhBand = (bandKey) => {
        setSelectedAhBands((previous) => (
            previous.includes(bandKey)
                ? previous.filter((item) => item !== bandKey)
                : [...previous, bandKey]
        ));
    };

    const showAllAhBands = () => setSelectedAhBands(AH_BANDS.map((band) => band.key));
    const reportId = useMemo(() => new URLSearchParams(search).get("id") || "-", [search]);
    const generatedOn = useMemo(
        () => new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
        []
    );
    const inspectionTargetYear = failedYear === 2010 ? "2060" : String(failedYear);
    const projectionHorizon = years.length ? `${years[0]} - ${years[years.length - 1]}` : "N/A";
    const totalSystemFrequency = useMemo(
        () => Object.values(inspectionYear).reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0),
        [inspectionYear]
    );
    const contactWebsite = "rbranalytics.io";
    const contactPhone = "+1 (888) 268-5374";
    const contactEmail = "info@rbranalytics.io";
    const observationsToShow = observations.length
        ? observations
        : ["AI Comments (Beta) will appear here once report analytics are available."];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox my={3} id={"screenbar"} className={"report-shell"}>
                <Card className={"report-card"}>
                    <SoftBox p={{ xs: 2, md: 4 }}>
                        <SoftBox className={"report-hero"}>
                            <SoftBox className={"report-hero-top"}>
                                <SoftBox className={"report-hero-main"}>
                                    <SoftTypography className={"report-kicker"}>
                                        Engineering Intelligence | Reliability Consulting
                                    </SoftTypography>
                                    <SoftTypography variant={"h3"} fontWeight={"bold"} className={"report-hero-title"}>
                                        Risk-Based Reliability Assessment
                                    </SoftTypography>
                                    <SoftTypography variant={"body2"} className={"report-hero-subtitle"}>
                                        Structured analytics for operations, maintenance, integrity, and risk decisions.
                                    </SoftTypography>
                                    <SoftBox className={"report-badges"}>
                                        <SoftBox component={"span"} className={"report-badge"}>Report ID: {reportId}</SoftBox>
                                        <SoftBox component={"span"} className={"report-badge"}>Generated: {generatedOn}</SoftBox>
                                        <SoftBox component={"span"} className={"report-badge"}>Horizon: {projectionHorizon}</SoftBox>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox className={"report-brand-panel"}>
                                    <SoftBox component="img" src={"/media/images/logo_dark.png"} alt="RBR Analytics" className={"report-brand-logo"} />
                                    <SoftTypography className={"report-brand-line"}>
                                        <a className={"report-contact-link"} href={`https://${contactWebsite}`} target={"_blank"} rel={"noreferrer"}>
                                            {contactWebsite}
                                        </a>
                                    </SoftTypography>
                                    <SoftTypography className={"report-brand-line"}>
                                        <a className={"report-contact-link"} href={"tel:+18882685374"}>
                                            {contactPhone}
                                        </a>
                                    </SoftTypography>
                                    <SoftTypography className={"report-brand-line"}>
                                        <a className={"report-contact-link"} href={`mailto:${contactEmail}`}>
                                            {contactEmail}
                                        </a>
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>

                        <Grid container spacing={2} mt={0.5}>
                            <Grid item xs={12} sm={6} md={3}>
                                <SoftBox className={"report-metric-card"}>
                                    <SoftTypography className={"report-metric-label"}>Assets Analyzed</SoftTypography>
                                    <SoftTypography className={"report-metric-value"}>{recordCount}</SoftTypography>
                                    <SoftTypography className={"report-metric-caption"}>
                                        Distinct asset-level rows included in the AH model.
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SoftBox className={"report-metric-card"}>
                                    <SoftTypography className={"report-metric-label"}>Inspection Target</SoftTypography>
                                    <SoftTypography className={"report-metric-value"}>{inspectionTargetYear}</SoftTypography>
                                    <SoftTypography className={"report-metric-caption"}>
                                        Earliest predicted system-level threshold year.
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SoftBox className={"report-metric-card"}>
                                    <SoftTypography className={"report-metric-label"}>Model Years</SoftTypography>
                                    <SoftTypography className={"report-metric-value"}>{years.length}</SoftTypography>
                                    <SoftTypography className={"report-metric-caption"}>
                                        Projected annual points in the reliability window.
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SoftBox className={"report-metric-card"}>
                                    <SoftTypography className={"report-metric-label"}>System Frequency Sum</SoftTypography>
                                    <SoftTypography className={"report-metric-value"}>{totalSystemFrequency.toFixed(4)}</SoftTypography>
                                    <SoftTypography className={"report-metric-caption"}>
                                        Aggregated projected system failure frequency.
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3} mt={0.25}>
                            <Grid item xs={12} md={8}>
                                <SoftBox className={"report-section-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Executive Summary
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Consultant Notes and AI Comments (Beta)
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-text"}>
                                        This assessment was generated using your selected asset scope and reliability model.
                                        Results combine data-derived indicators with domain-oriented interpretation to support
                                        inspection planning, risk reduction, and capital maintenance strategy.
                                    </SoftTypography>
                                    <SoftBox className={"report-observations"}>
                                        {observationsToShow.map((item, index) => (
                                            <SoftBox key={`obs-${index}`} className={"report-observation-item"}>
                                                <SoftBox className={"report-observation-index"}>{index + 1}</SoftBox>
                                                <SoftTypography variant={"body2"} className={"report-observation-text"}>
                                                    {item}
                                                </SoftTypography>
                                            </SoftBox>
                                        ))}
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <SoftBox className={"report-section-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Advisory Scope
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Engineering Support Options
                                    </SoftTypography>
                                    <SoftBox className={"report-service-list"}>
                                        <SoftTypography variant={"body2"} className={"report-service-item"}>
                                            End-to-end RBI programs for equipment, piping, and facility systems.
                                        </SoftTypography>
                                        <SoftTypography variant={"body2"} className={"report-service-item"}>
                                            Method customization by damage mechanism, fluid behavior, and asset context.
                                        </SoftTypography>
                                        <SoftTypography variant={"body2"} className={"report-service-item"}>
                                            Data quality improvement and gap closure workflows for integrity teams.
                                        </SoftTypography>
                                        <SoftTypography variant={"body2"} className={"report-service-item"}>
                                            Inspection optimization and risk-ranked intervention planning.
                                        </SoftTypography>
                                        <SoftTypography variant={"body2"} className={"report-service-item"}>
                                            Compliance-aligned recommendations for frequencies and action priorities.
                                        </SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox className={"report-section-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Figure 1
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Asset Age Distribution
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-text"}>
                                        This view highlights age concentration across the analyzed population and helps prioritize
                                        maintenance campaigns by lifecycle stage.
                                    </SoftTypography>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={option_mergers}
                                        allowChartUpdate={true}
                                        immutable={false}
                                    />
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox className={"report-section-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Figure 2
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Asset Health Predictive Analytics
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-text"}>
                                        The heatmap estimates years-to-failure by segment and forecast year. AH bands are:
                                        AH5 (&lt;=5), AH4 (&lt;=10), AH3 (&lt;=25), AH2 (&lt;=40), AH1 (&gt;40 years). Use the filters
                                        below to isolate risk classes.
                                    </SoftTypography>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={option_analytics}
                                        allowChartUpdate={true}
                                        immutable={false}
                                    />
                                    <SoftBox mt={2} display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                                        {AH_BANDS.map((band) => {
                                            const active = selectedAhBandSet.has(band.key);
                                            return (
                                                <SoftButton
                                                    key={band.key}
                                                    size="small"
                                                    variant={active ? "gradient" : "outlined"}
                                                    color={active ? "info" : "secondary"}
                                                    onClick={() => toggleAhBand(band.key)}
                                                >
                                                    <span
                                                        style={{
                                                            width: "10px",
                                                            height: "10px",
                                                            backgroundColor: band.color,
                                                            borderRadius: "2px",
                                                            marginRight: "8px",
                                                            display: "inline-block",
                                                        }}
                                                    />
                                                    {band.label}
                                                </SoftButton>
                                            );
                                        })}
                                        <SoftButton size="small" variant="text" color="dark" onClick={showAllAhBands}>
                                            Show All
                                        </SoftButton>
                                    </SoftBox>
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox className={"report-section-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Figure 3
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Risk-Based Inspection Projection
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-text"}>
                                        System-level accumulated failure frequency is projected across the model horizon.
                                        Based on the current scenario, proactive inspection is recommended before {inspectionTargetYear}
                                        to reduce risk exposure.
                                    </SoftTypography>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={option_line}
                                        allowChartUpdate={true}
                                        immutable={false}
                                    />
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox className={"report-recommendation-card"}>
                                    <SoftTypography className={"report-section-kicker"}>
                                        Recommendation
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-title"}>
                                        Inspection and Risk Mitigation Action
                                    </SoftTypography>
                                    <SoftTypography className={"report-section-text"}>
                                        According to the predictive RBI results, schedule the next inspection prior to{" "}
                                        <strong>{inspectionTargetYear}</strong>. A targeted inspection campaign in{" "}
                                        {new Date().getFullYear()} can materially reduce near-term system risk and improve
                                        reliability planning confidence.
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>

                        <SoftBox mt={3} display={"flex"} justifyContent={"space-between"} alignItems={"center"} flexWrap={"wrap"} gap={2}>
                            <SoftBox component="img" src={"/media/images/logo_dark.png"} alt="RBR-Dashboard" width="10rem" />
                            <SoftTypography variant={"caption"} color={"secondary"} className={"report-footer-contact"}>
                                <a href={`https://${contactWebsite}`} target={"_blank"} rel={"noreferrer"}>{contactWebsite}</a>
                                {" | "}
                                <a href={"tel:+18882685374"}>{contactPhone}</a>
                                {" | "}
                                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                            </SoftTypography>
                        </SoftBox>

                        <SoftBox className={"report-actions"} data-html2canvas-ignore={"true"}>
                            <SoftButton
                                size={"large"}
                                variant={"outlined"}
                                color={"dark"}
                                onClick={() => {
                                    const element = document.querySelector("#screenbar");
                                    html2canvas(element, { quality: 0.95, backgroundColor:"#FFFFFF", removeContainer:true })
                                        .then((canvas) => {
                                            const pdf = new jsPDF("p", "mm", "a4");
                                            const imgData = canvas.toDataURL("image/jpeg", 0.95);
                                            const pdfWidth = pdf.internal.pageSize.getWidth();
                                            const pdfHeight = pdf.internal.pageSize.getHeight();
                                            const imgWidth = pdfWidth;
                                            const imgHeight = (canvas.height * imgWidth) / canvas.width;
                                            let heightLeft = imgHeight;
                                            let position = 0;

                                            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                                            heightLeft -= pdfHeight;

                                            while (heightLeft > 0) {
                                                position = heightLeft - imgHeight;
                                                pdf.addPage();
                                                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
                                                heightLeft -= pdfHeight;
                                            }
                                            pdf.save(`report-${reportId}.pdf`);
                                        })
                                        .catch((err) => {
                                            console.log(err);
                                        });
                                }}
                            >
                                <Icon>print</Icon> Export PDF
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </Card>
            </SoftBox>
            <Footer />
        </DashboardLayout>
    )
}

export default ReportViewer;
